import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  List,
  ListItemButton,
  ListItemText,
  ListItem,
  Button,
  Tooltip,
  CircularProgress,
  Divider,
  TextField,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import StorageIcon from '@mui/icons-material/Storage';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { useTranslation } from 'react-i18next';
import { getPathRootLabel, presentActionPath } from '../Utils/actionPathPresentation';
import OtherViewer from './Viewer/OtherViewer';
import PDFViewer from './Viewer/PDFViewer';
import ImageViewer from './Viewer/ImageViewer';
import AudioViewer from './Viewer/AudioViewer';
import ExcelViewer from './Viewer/ExcelViewer';
import HTMLViewer from './Viewer/HTMLViewer';
import TextViewer from './Viewer/TextViewer';
import VideoViewer from './Viewer/VideoViewer';
import EmailViewer from './Viewer/EmailViewer';
import ZipViewer from './Viewer/ZipViewer';
import WordViewer from './Viewer/WordViewer';
import PowerPointViewer from './Viewer/PowerPointViewer';
import CSVViewer from './Viewer/CSVViewer';
import JSONViewer from './Viewer/JSONViewer';
import useStorageUpload from './useStorageUpload';
import StorageBrowserReplaceDialog from './StorageBrowserReplaceDialog';

export const isStoragePreviewableFile = (fileName) => {
  const isExt = (name, exts = []) => {
    const lower = (name || '').toLowerCase();
    return exts.some((ext) => lower.endsWith(ext));
  };

  return (
    isExt(fileName, ['.pdf']) ||
    isExt(fileName, ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff', '.webp', '.avif']) ||
    isExt(fileName, ['.mp3', '.wav', '.ogg', '.m4a', '.flac', '.aac', '.wma']) ||
    isExt(fileName, ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v', '.3gp', '.ogv']) ||
    isExt(fileName, ['.txt', '.log', '.md', '.ini', '.yml', '.yaml', '.sql', '.conf', '.cfg', '.properties', '.csv', '.json']) ||
    isExt(fileName, ['.xlsx', '.xls', '.xlsm']) ||
    isExt(fileName, ['.docx', '.doc'])
  );
};

const StorageBrowser = ({
  open,
  onClose,
  displayPath = '/',
  folders = [],
  files = [],
  loading = false,
  error = '',
  hasLoaded = false,
  onDismissError = () => {},
  onFolderClick = () => {},
  onNavigateUp = () => {},
  onNavigate = () => {},
  onRefresh = () => {},
  onCreateFolder = () => {},
  onDeleteFile = () => {},
  onDeleteFolder = () => {},
  onRenameFile = () => {},
  onRenameFolder = () => {},
  onDownload = () => {},
  onDownloadAll = () => {},
  currentPath = '',
  canManageStorage = false,
  canWriteStorage = false,
  primaryColor = '#5757b0',
  downloadingFolder = false,
  restrictToUserFolder = false,
  allowedRoots = [],
  activeRoot = '',
  activeRootLabel = '',
  onRootChange = () => {}
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [newFolderName, setNewFolderName] = React.useState('');
  const [folderSortMode, setFolderSortMode] = React.useState('name');
  const [renameOpen, setRenameOpen] = React.useState(false);
  const [renameTarget, setRenameTarget] = React.useState(null);
  const [renameValue, setRenameValue] = React.useState('');
  const [sortMode, setSortMode] = React.useState('name');
  const [viewerOpen, setViewerOpen] = React.useState(false);
  const [viewerFile, setViewerFile] = React.useState(null);
  const [pdfViewer, setPdfViewer] = React.useState({ open: false, name: '', path: '' });
  const [imageViewer, setImageViewer] = React.useState({ open: false, name: '', path: '' });
  const [audioViewer, setAudioViewer] = React.useState({ open: false, name: '', path: '' });
  const [excelViewer, setExcelViewer] = React.useState({ open: false, name: '', path: '' });
  const [htmlViewer, setHtmlViewer] = React.useState({ open: false, name: '', path: '' });
  const [textViewer, setTextViewer] = React.useState({ open: false, name: '', path: '' });
  const [videoViewer, setVideoViewer] = React.useState({ open: false, name: '', path: '' });
  const [emailViewer, setEmailViewer] = React.useState({ open: false, name: '', path: '' });
  const [zipViewer, setZipViewer] = React.useState({ open: false, name: '', path: '' });
  const [wordViewer, setWordViewer] = React.useState({ open: false, name: '', path: '' });
  const [pptViewer, setPptViewer] = React.useState({ open: false, name: '', path: '' });
  const [csvViewer, setCsvViewer] = React.useState({ open: false, name: '', path: '' });
  const [jsonViewer, setJsonViewer] = React.useState({ open: false, name: '', path: '' });
  const [isDragActive, setIsDragActive] = React.useState(false);
  const dragDepthRef = React.useRef(0);
  const [confirmReplaceOpen, setConfirmReplaceOpen] = React.useState(false);
  const [pendingUploadFiles, setPendingUploadFiles] = React.useState([]);
  const [duplicateUploadNames, setDuplicateUploadNames] = React.useState([]);
  const [selectedItems, setSelectedItems] = React.useState(() => new Set());
  const lastSelectedKeyRef = React.useRef(null);
  const canModifyStorage = Boolean(canWriteStorage);
  const pathRoots = React.useMemo(() => allowedRoots.map((path, index) => ({
    path, kind: index === 0 ? 'storage' : 'shared',
  })), [allowedRoots]);
  const getStorageRootLabel = (root) => {
    if (root === activeRoot && activeRootLabel) return activeRootLabel;
    const entry = pathRoots.find((item) => item.path === root);
    return entry ? getPathRootLabel(entry, pathRoots, t) : t('workflow.paths.saved_location');
  };
  const storageUploadTarget = currentPath || '';
  const storageUploadMeta = React.useMemo(() => ({
    path: storageUploadTarget
  }), [storageUploadTarget]);
  const {
    uploading: storageUploading,
    uploadProgress: storageUploadProgress,
    inputRef: storageUploadInputRef,
    handleFiles: handleStorageFiles,
    triggerFileDialog: openStorageUploadDialog
  } = useStorageUpload({
    command: 'uploadStorageFile',
    meta: storageUploadMeta,
    httpEndpoint: '/api/v1/storage/upload',
    successMessage: t('sidebar.storage_upload_success', 'File uploaded to storage'),
    errorMessage: t('sidebar.storage_upload_error', 'Failed to upload file'),
    getErrorMessage: (error) => error?.code === 'storage_quota_exceeded'
      ? t('sidebar.storage_quota_exceeded', 'This organization has reached its storage quota. Delete files or ask a platform administrator to raise the limit.')
      : '',
    sendingMessage: t('sidebar.storage_uploading', 'Uploading file...'),
    onSuccess: () => {
      if (onRefresh) {
        onRefresh();
      }
    }
  });

  const storageUploadPercent = Math.min(
    100,
    Math.max(
      0,
      Number.isFinite(storageUploadProgress) ? storageUploadProgress : 0
    )
  );

  const existingFileNames = React.useMemo(() => {
    if (!Array.isArray(files)) return new Set();
    return new Set(files.map((file) => (file?.name || '').toLowerCase()));
  }, [files]);

  const resetPendingUpload = () => {
    setPendingUploadFiles([]);
    setDuplicateUploadNames([]);
    setConfirmReplaceOpen(false);
  };

  const handleFilesWithConfirm = (incomingFiles) => {
    if (!canModifyStorage) return;
    const fileList = Array.isArray(incomingFiles) ? incomingFiles : Array.from(incomingFiles || []);
    if (!fileList.length) return;
    const duplicates = fileList
      .map((file) => file?.name || '')
      .filter((name) => name && existingFileNames.has(name.toLowerCase()));
    if (duplicates.length > 0) {
      setPendingUploadFiles(fileList);
      setDuplicateUploadNames(duplicates);
      setConfirmReplaceOpen(true);
      return;
    }
    handleStorageFiles(fileList);
  };

  const canDropUpload = Boolean(canModifyStorage && storageUploadTarget) && !storageUploading;
  const normalizedDisplayPath = React.useMemo(() => {
    const raw = typeof displayPath === 'string' ? displayPath : '';
    const normalized = presentActionPath(raw, pathRoots, { storageRelative: true }).display.replace(/\\/g, '/');
    return normalized ? `/${normalized.replace(/^\/+/, '')}` : '/';
  }, [displayPath, pathRoots]);
  const displayPathSegments = React.useMemo(
    () => normalizedDisplayPath.split('/').filter(Boolean),
    [normalizedDisplayPath]
  );
  const isDisplayRooted = normalizedDisplayPath.startsWith('/');
  const canNavigateUp = displayPathSegments.length > 0;

  const buildBreadcrumbTarget = React.useCallback((segmentIndex) => {
    if (segmentIndex < 0) {
      return isDisplayRooted ? '/' : '';
    }
    const joined = displayPathSegments.slice(0, segmentIndex + 1).join('/');
    return isDisplayRooted ? `/${joined}` : joined;
  }, [displayPathSegments, isDisplayRooted]);

  const handleBreadcrumbNavigate = React.useCallback((targetPath) => {
    if (!onNavigate) return;
    onNavigate(targetPath || '/');
  }, [onNavigate]);

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canDropUpload) return;
    dragDepthRef.current += 1;
    setIsDragActive(true);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canDropUpload) return;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragActive(false);
    if (!canDropUpload) return;
    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) return;
    handleFilesWithConfirm(files);
  };

  const sortedFolders = React.useMemo(() => {
    const list = Array.isArray(folders) ? [...folders] : [];
    const sorted = list.sort((a, b) => (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' }));
    if (folderSortMode === 'name_desc') {
      return sorted.reverse();
    }
    return sorted;
  }, [folders, folderSortMode]);

  const sortedFiles = React.useMemo(() => {
    const list = Array.isArray(files) ? [...files] : [];
    const getTime = (f, field) => {
      const timestamp = f?.[field] || (field === 'modifiedAt' ? f?.createdAt : '');
      if (!timestamp) return 0;
      const ts = new Date(timestamp).getTime();
      return Number.isNaN(ts) ? 0 : ts;
    };
    if (sortMode === 'created_desc') {
      return list.sort((a, b) => getTime(b, 'createdAt') - getTime(a, 'createdAt'));
    }
    if (sortMode === 'created_asc') {
      return list.sort((a, b) => getTime(a, 'createdAt') - getTime(b, 'createdAt'));
    }
    if (sortMode === 'modified_desc') {
      return list.sort((a, b) => getTime(b, 'modifiedAt') - getTime(a, 'modifiedAt'));
    }
    if (sortMode === 'modified_asc') {
      return list.sort((a, b) => getTime(a, 'modifiedAt') - getTime(b, 'modifiedAt'));
    }
    if (sortMode === 'name_desc') {
      return list.sort((a, b) => (b?.name || '').localeCompare(a?.name || '', undefined, { sensitivity: 'base' }));
    }
    // name asc default
    return list.sort((a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' }));
  }, [files, sortMode]);

  React.useEffect(() => {
    setSelectedItems(new Set());
    lastSelectedKeyRef.current = null;
  }, [currentPath, folders, files]);

  const getSelectionKey = React.useCallback((type, name) => `${type}:${name}`, []);

  const selectableItems = React.useMemo(() => [
    ...sortedFolders.map((name) => ({ type: 'folder', name })),
    ...sortedFiles.map((file) => ({ type: 'file', name: file?.name || '' })).filter((item) => item.name)
  ], [sortedFolders, sortedFiles]);

  const selectedItemList = React.useMemo(() => {
    const currentItems = new Map(selectableItems.map((item) => [getSelectionKey(item.type, item.name), item]));
    return Array.from(selectedItems)
      .map((key) => currentItems.get(key))
      .filter(Boolean);
  }, [getSelectionKey, selectableItems, selectedItems]);

  const selectedCount = selectedItemList.length;
  const folderSelectionKeys = React.useMemo(
    () => sortedFolders.map((name) => getSelectionKey('folder', name)),
    [getSelectionKey, sortedFolders]
  );
  const fileSelectionKeys = React.useMemo(
    () => sortedFiles.map((file) => getSelectionKey('file', file?.name || '')).filter(Boolean),
    [getSelectionKey, sortedFiles]
  );
  const allFoldersSelected = folderSelectionKeys.length > 0 && folderSelectionKeys.every((key) => selectedItems.has(key));
  const someFoldersSelected = folderSelectionKeys.some((key) => selectedItems.has(key));
  const allFilesSelected = fileSelectionKeys.length > 0 && fileSelectionKeys.every((key) => selectedItems.has(key));
  const someFilesSelected = fileSelectionKeys.some((key) => selectedItems.has(key));

  const toggleSelection = (type, name, event) => {
    if (!canModifyStorage || !name) return;
    const key = getSelectionKey(type, name);
    const shiftPressed = Boolean(event?.shiftKey || event?.nativeEvent?.shiftKey);
    const nextChecked = event?.target ? Boolean(event.target.checked) : null;
    setSelectedItems((previous) => {
      const next = new Set(previous);
      const selectionKeys = selectableItems.map((item) => getSelectionKey(item.type, item.name));
      const currentIndex = selectionKeys.indexOf(key);
      const anchorIndex = selectionKeys.indexOf(lastSelectedKeyRef.current);

      if (shiftPressed && currentIndex >= 0 && anchorIndex >= 0) {
        const [start, end] = currentIndex < anchorIndex
          ? [currentIndex, anchorIndex]
          : [anchorIndex, currentIndex];
        selectionKeys.slice(start, end + 1).forEach((rangeKey) => {
          if (nextChecked === false) {
            next.delete(rangeKey);
          } else {
            next.add(rangeKey);
          }
        });
      } else if (nextChecked !== null) {
        if (nextChecked) {
          next.add(key);
        } else {
          next.delete(key);
        }
      } else if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    lastSelectedKeyRef.current = key;
  };

  const toggleSelectionGroup = (keys, checked) => {
    if (!canModifyStorage) return;
    lastSelectedKeyRef.current = null;
    setSelectedItems((previous) => {
      const next = new Set(previous);
      keys.forEach((key) => {
        if (checked) {
          next.add(key);
        } else {
          next.delete(key);
        }
      });
      return next;
    });
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  };

  const formatFileSize = (value) => {
    const bytes = Number(value);
    if (!Number.isFinite(bytes) || bytes < 0) return '—';
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const size = bytes / (1024 ** unitIndex);
    const precision = unitIndex === 0 ? 0 : size >= 10 ? 1 : 2;
    return `${size.toFixed(precision)} ${units[unitIndex]}`;
  };

  const buildFullPath = (name) => {
    if (!name || !currentPath) return '';
    const separator = currentPath.includes('\\') ? '\\' : '/';
    const trimmedBase = currentPath.endsWith(separator) ? currentPath.slice(0, -1) : currentPath;
    return `${trimmedBase}${separator}${name}`;
  };

  const isExt = (fileName, exts = []) => {
    const lower = (fileName || '').toLowerCase();
    return exts.some((ext) => lower.endsWith(ext));
  };

  const handleOpenViewer = (fileName) => {
    const fullPath = buildFullPath(fileName);
    if (!fullPath) {
      console.warn('[StorageBrowser] Missing path for file', fileName);
      return;
    }

    const openDownloadViewer = () => {
      setViewerFile({ name: fileName, path: fullPath });
      setViewerOpen(true);
    };

    if (restrictToUserFolder && !isStoragePreviewableFile(fileName)) {
      openDownloadViewer();
      return;
    }

    const openViewer = (setter) => {
      setter({ open: true, name: fileName, path: fullPath });
    };

    if (isExt(fileName, ['.pdf'])) {
      openViewer(setPdfViewer);
    } else if (isExt(fileName, ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff', '.webp', '.avif'])) {
      openViewer(setImageViewer);
    } else if (isExt(fileName, ['.mp3', '.wav', '.ogg', '.m4a', '.flac', '.aac', '.wma'])) {
      openViewer(setAudioViewer);
    } else if (isExt(fileName, ['.xlsx', '.xls', '.xlsm'])) {
      openViewer(setExcelViewer);
    } else if (isExt(fileName, ['.html', '.htm'])) {
      openViewer(setHtmlViewer);
    } else if (isExt(fileName, ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v', '.3gp', '.ogv'])) {
      openViewer(setVideoViewer);
    } else if (isExt(fileName, ['.msg', '.eml'])) {
      openViewer(setEmailViewer);
    } else if (isExt(fileName, ['.zip', '.rar', '.7z', '.tar', '.gz', '.bz2'])) {
      openViewer(setZipViewer);
    } else if (isExt(fileName, ['.docx', '.doc'])) {
      openViewer(setWordViewer);
    } else if (isExt(fileName, ['.pptx', '.ppt'])) {
      openViewer(setPptViewer);
    } else if (isExt(fileName, ['.csv'])) {
      openViewer(setCsvViewer);
    } else if (isExt(fileName, ['.json'])) {
      openViewer(setJsonViewer);
    } else if (isExt(fileName, ['.txt', '.log', '.md', '.ini', '.yml', '.yaml', '.sql', '.conf', '.cfg', '.properties'])) {
      openViewer(setTextViewer);
    } else {
      setViewerFile({ name: fileName, path: fullPath });
      setViewerOpen(true);
    }
  };

  const closeJsonViewer = React.useCallback(() => {
    setJsonViewer({ open: false, name: '', path: '' });
  }, []);

  const openDeleteConfirm = (name, type) => {
    if (!canModifyStorage) return;
    setDeleteTarget({ name, type });
  };

  const openBulkDeleteConfirm = () => {
    if (!canModifyStorage || selectedItemList.length === 0) return;
    setDeleteTarget({ type: 'bulk', items: selectedItemList });
  };

  const openRenameDialog = (name, type) => {
    if (!canModifyStorage) return;
    setRenameTarget({ name, type });
    setRenameValue(name || '');
    setRenameOpen(true);
  };

  const closeDeleteConfirm = () => {
    setDeleteTarget(null);
  };

  const confirmDelete = () => {
    if (!canModifyStorage) {
      closeDeleteConfirm();
      return;
    }
    if (deleteTarget?.type === 'bulk') {
      (deleteTarget.items || []).forEach((item) => {
        if (item.type === 'folder') {
          onDeleteFolder(item.name);
        } else if (item.type === 'file') {
          onDeleteFile(item.name);
        }
      });
      setSelectedItems(new Set());
      lastSelectedKeyRef.current = null;
    } else if (deleteTarget?.type === 'folder') {
      onDeleteFolder(deleteTarget.name);
    } else if (deleteTarget?.type === 'file') {
      onDeleteFile(deleteTarget.name);
    }
    setDeleteTarget(null);
  };

  const closeRenameDialog = () => {
    setRenameOpen(false);
    setRenameTarget(null);
    setRenameValue('');
  };

  const handleRenameSubmit = () => {
    if (!canModifyStorage) {
      closeRenameDialog();
      return;
    }
    const trimmed = (renameValue || '').trim();
    if (!trimmed || !renameTarget?.name) {
      closeRenameDialog();
      return;
    }
    if (renameTarget.type === 'folder') {
      onRenameFolder(renameTarget.name, trimmed);
    } else if (renameTarget.type === 'file') {
      onRenameFile(renameTarget.name, trimmed);
    }
    closeRenameDialog();
  };

  const handleCreateSubmit = () => {
    if (!canModifyStorage) return;
    const trimmed = (newFolderName || '').trim();
    if (!trimmed) return;
    onCreateFolder(trimmed);
    setCreateOpen(false);
    setNewFolderName('');
  };
  if (!canManageStorage) {
    return null;
  }

  const renderContent = () => {
    if (error && !hasLoaded) {
      return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <Alert severity="error" role="alert">{error}</Alert>
          <Button onClick={onRefresh} sx={{ mt: 2 }}>
            {t('sidebar.storage_retry', 'Try again')}
          </Button>
        </Box>
      );
    }

    if (loading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, minHeight: 280 }}>
          <CircularProgress size={22} sx={{ color: primaryColor }} />
          <Typography variant="body2" sx={{ color: '#687087' }}>
            {t('sidebar.storage_loading', 'Loading storage...')}
          </Typography>
        </Box>
      );
    }

    const accentColor = primaryColor || '#5757b0';
    const panelBorder = '1px solid rgba(63, 67, 108, 0.12)';
    const folderGridTemplateColumns = canModifyStorage ? '34px minmax(0, 1fr) 96px' : 'minmax(0, 1fr) 64px';
    const fileGridTemplateColumns = canModifyStorage
      ? { xs: '34px minmax(0, 1fr) 40px', md: '34px minmax(240px, 1fr) 145px 86px 145px 124px' }
      : { xs: 'minmax(0, 1fr) 40px', md: 'minmax(240px, 1fr) 145px 86px 145px 72px' };
    const toolbarIconSx = {
      width: 34,
      height: 34,
      color: '#5d6378',
      border: '1px solid rgba(63, 67, 108, 0.12)',
      borderRadius: 1.5,
      '&:hover': { color: accentColor, backgroundColor: 'rgba(87, 87, 176, 0.07)' }
    };
    const rowIconSx = {
      width: 28,
      height: 28,
      color: '#74798d',
      '&:hover': { color: accentColor, backgroundColor: 'rgba(87, 87, 176, 0.08)' }
    };
    const panelHeaderSx = {
      px: { xs: 1.5, md: 2 },
      py: 1.25,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: panelBorder,
      backgroundColor: '#fbfbfe'
    };
    const columnHeaderSx = {
      display: 'grid',
      alignItems: 'center',
      px: { xs: 1, md: 1.5 },
      py: 1,
      color: '#777c91',
      backgroundColor: '#f3f4fa',
      borderBottom: '1px solid rgba(63, 67, 108, 0.1)',
      '& .MuiTypography-root': {
        fontSize: '0.68rem',
        fontWeight: 800,
        letterSpacing: '0.07em',
        textTransform: 'uppercase'
      }
    };
    const rowSx = {
      display: 'grid',
      alignItems: 'center',
      columnGap: { xs: 0.5, md: 1 },
      minHeight: 54,
      px: { xs: 1, md: 1.5 },
      borderBottom: '1px solid rgba(63, 67, 108, 0.07)',
      borderRadius: 0,
      color: '#303548',
      '&:hover': { backgroundColor: 'rgba(87, 87, 176, 0.065)' },
      '&.Mui-selected': {
        backgroundColor: 'rgba(87, 87, 176, 0.10)',
        '&:hover': { backgroundColor: 'rgba(87, 87, 176, 0.14)' }
      }
    };

    return (
      <>
        {error && (
          <Alert severity="error" onClose={onDismissError} closeText={t('common.close', 'Close')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {canModifyStorage && selectedCount > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 1.5,
              px: 1.5,
              py: 1,
              border: '1px solid rgba(87, 87, 176, 0.18)',
              borderRadius: 2,
              backgroundColor: 'rgba(87, 87, 176, 0.07)'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 800, color: '#3f4262' }}>
              {t('sidebar.storage_selected_count', '{{count}} selected', { count: selectedCount })}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Button
                size="small"
                onClick={() => {
                  setSelectedItems(new Set());
                  lastSelectedKeyRef.current = null;
                }}
                sx={{ color: '#5f6479', textTransform: 'none', fontWeight: 700 }}
              >
                {t('sidebar.storage_clear_selection', 'Clear')}
              </Button>
              <Button
                size="small"
                color="error"
                variant="contained"
                startIcon={<DeleteIcon />}
                onClick={openBulkDeleteConfirm}
                sx={{ borderRadius: 1.5, textTransform: 'none', boxShadow: 'none' }}
              >
                {t('sidebar.storage_delete_selected', 'Delete selected')}
              </Button>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
            p: 0.75,
            border: panelBorder,
            borderRadius: 2,
            backgroundColor: '#fff',
            boxShadow: '0 2px 8px rgba(37, 41, 83, 0.04)'
          }}
        >
          <Tooltip title={t('sidebar.storage_up', 'Go up')}>
            <span>
              <IconButton onClick={onNavigateUp} disabled={!canNavigateUp} sx={toolbarIconSx} aria-label={t('sidebar.storage_up', 'Go up')}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('sidebar.storage_refresh', 'Refresh')}>
            <span>
              <IconButton onClick={onRefresh} sx={toolbarIconSx} aria-label={t('sidebar.storage_refresh', 'Refresh')}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              minWidth: { xs: '100%', sm: 220 },
              overflow: 'hidden',
              px: 1,
              py: 0.65,
              border: '1px solid rgba(63, 67, 108, 0.12)',
              borderRadius: 1.5,
              backgroundColor: '#f8f8fc'
            }}
          >
            <Tooltip title={t('sidebar.storage_root_default', 'Root')}>
              <IconButton
                size="small"
                onClick={() => handleBreadcrumbNavigate(isDisplayRooted ? '/' : '')}
                sx={{ color: accentColor, mr: 0.35 }}
                aria-label={t('sidebar.storage_root_default', 'Root')}
              >
                <HomeOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, overflowX: 'auto', whiteSpace: 'nowrap' }}>
              {displayPathSegments.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#4f5369', fontWeight: 700 }}>
                  {t('sidebar.storage_root_default', 'Root')}
                </Typography>
              ) : (
                displayPathSegments.map((segment, index) => (
                  <React.Fragment key={`${segment}-${index}`}>
                    <ChevronRightIcon sx={{ fontSize: 17, color: '#9da1b1', mx: 0.15 }} />
                    <Button
                      size="small"
                      onClick={() => handleBreadcrumbNavigate(buildBreadcrumbTarget(index))}
                      sx={{
                        minWidth: 'auto',
                        px: 0.5,
                        py: 0.15,
                        color: index === displayPathSegments.length - 1 ? '#3f4262' : '#6e7488',
                        textTransform: 'none',
                        fontSize: '0.78rem',
                        fontWeight: index === displayPathSegments.length - 1 ? 800 : 600,
                        '&:hover': { backgroundColor: 'rgba(87, 87, 176, 0.08)' }
                      }}
                    >
                      {segment}
                    </Button>
                  </React.Fragment>
                ))
              )}
            </Box>
          </Box>
          <Typography
            variant="caption"
            sx={{ display: { xs: 'none', md: 'block' }, color: '#7b8093', px: 1, whiteSpace: 'nowrap' }}
          >
            {sortedFolders.length + sortedFiles.length} {t('sidebar.storage_items', 'items')}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(230px, 0.82fr) minmax(0, 2fr)' }, gap: 2 }}>
          <Box sx={{ border: panelBorder, borderRadius: 2.5, overflow: 'hidden', backgroundColor: '#fff', minWidth: 0 }}>
            <Box sx={panelHeaderSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'grid', placeItems: 'center', width: 32, height: 32, borderRadius: 1.5, backgroundColor: 'rgba(87, 87, 176, 0.11)', color: accentColor }}>
                  <FolderIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#35394f', fontWeight: 800, lineHeight: 1.2 }}>
                    {t('sidebar.storage_folders', 'Folders')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#858a9b' }}>
                    {sortedFolders.length} {t('sidebar.storage_items', 'items')}
                  </Typography>
                </Box>
              </Box>
              {canModifyStorage && (
                <Tooltip title={t('sidebar.storage_new_folder', 'New folder')}>
                  <span>
                    <IconButton size="small" onClick={() => setCreateOpen(true)} sx={toolbarIconSx} aria-label={t('sidebar.storage_new_folder', 'New folder')}>
                      <CreateNewFolderIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ ...columnHeaderSx, gridTemplateColumns: folderGridTemplateColumns }}>
              {canModifyStorage && (
                <Checkbox
                  size="small"
                  checked={allFoldersSelected}
                  indeterminate={someFoldersSelected && !allFoldersSelected}
                  disabled={folderSelectionKeys.length === 0}
                  onChange={(event) => toggleSelectionGroup(folderSelectionKeys, event.target.checked)}
                  inputProps={{ 'aria-label': t('sidebar.storage_select_all_folders', 'Select all folders') }}
                  sx={{ p: 0.25 }}
                />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                <Typography>{t('sidebar.storage_folder_name', 'Name')}</Typography>
                <Tooltip title={t('sidebar.storage_sort_name', 'Sort by name')}>
                  <IconButton size="small" onClick={() => setFolderSortMode(folderSortMode === 'name_desc' ? 'name' : 'name_desc')} sx={{ p: 0.25, color: '#777c91' }}>
                    {folderSortMode === 'name_desc' ? <ArrowDownwardIcon sx={{ fontSize: 16 }} /> : <ArrowUpwardIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography sx={{ textAlign: 'right' }}>{t('common.actions', 'Actions')}</Typography>
            </Box>
            <List dense disablePadding>
              {sortedFolders.length === 0 ? (
                <ListItem sx={{ minHeight: 160, justifyContent: 'center' }}>
                  <ListItemText
                    sx={{ textAlign: 'center' }}
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    primary={t('sidebar.storage_no_folders', 'No folders')}
                  />
                </ListItem>
              ) : (
                sortedFolders.map((folder) => (
                  <ListItemButton
                    key={folder}
                    selected={selectedItems.has(getSelectionKey('folder', folder))}
                    onClick={() => onFolderClick(folder)}
                    sx={{ ...rowSx, gridTemplateColumns: folderGridTemplateColumns }}
                  >
                    {canModifyStorage && (
                      <Checkbox
                        size="small"
                        checked={selectedItems.has(getSelectionKey('folder', folder))}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => toggleSelection('folder', folder, event)}
                        inputProps={{ 'aria-label': t('sidebar.storage_select_folder', 'Select folder') }}
                        sx={{ p: 0.25 }}
                      />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <FolderIcon sx={{ color: accentColor, fontSize: 21, flexShrink: 0 }} />
                      <Tooltip title={folder} placement="top-start">
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
                          {folder}
                        </Typography>
                      </Tooltip>
                      <Tooltip title={t('sidebar.storage_open_folder', 'Open folder')}>
                        <IconButton size="small" onClick={(event) => { event.stopPropagation(); onFolderClick(folder); }} sx={{ ...rowIconSx, ml: 'auto', flexShrink: 0 }}>
                          <SubdirectoryArrowRightIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.15 }}>
                      {canModifyStorage && (
                        <>
                          <Tooltip title={t('sidebar.storage_rename_folder', 'Rename folder')}>
                            <IconButton size="small" onClick={(event) => { event.stopPropagation(); openRenameDialog(folder, 'folder'); }} sx={rowIconSx}>
                              <EditIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('sidebar.storage_delete_folder', 'Delete folder')}>
                            <IconButton size="small" onClick={(event) => { event.stopPropagation(); openDeleteConfirm(folder, 'folder'); }} sx={rowIconSx}>
                              <DeleteIcon sx={{ fontSize: 17 }} color="error" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </ListItemButton>
                ))
              )}
            </List>
          </Box>

          <Box sx={{ border: panelBorder, borderRadius: 2.5, overflow: 'hidden', backgroundColor: '#fff', minWidth: 0 }}>
            <Box sx={panelHeaderSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'grid', placeItems: 'center', width: 32, height: 32, borderRadius: 1.5, backgroundColor: 'rgba(87, 87, 176, 0.11)', color: accentColor }}>
                  <DescriptionIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#35394f', fontWeight: 800, lineHeight: 1.2 }}>
                    {t('sidebar.storage_files', 'Files')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#858a9b' }}>
                    {sortedFiles.length} {t('sidebar.storage_items', 'items')}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {canModifyStorage && (
                  <Tooltip title={storageUploading ? t('sidebar.storage_uploading', 'Uploading file...') : t('sidebar.storage_upload', 'Upload file')}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => { if (storageUploadTarget && !storageUploading) openStorageUploadDialog(); }}
                        disabled={!storageUploadTarget || storageUploading}
                        sx={toolbarIconSx}
                        aria-label={t('sidebar.storage_upload', 'Upload file')}
                      >
                        {storageUploading ? <CircularProgress size={17} /> : <UploadFileIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title={downloadingFolder ? t('sidebar.storage_downloading', 'Preparing download...') : t('sidebar.storage_download_all', 'Download all files in this folder')}>
                  <span>
                    <IconButton onClick={onDownloadAll} size="small" disabled={loading || downloadingFolder} sx={toolbarIconSx} aria-label={t('sidebar.storage_download_all', 'Download all files in this folder')}>
                      {downloadingFolder ? <CircularProgress size={17} /> : <FolderZipIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ ...columnHeaderSx, gridTemplateColumns: fileGridTemplateColumns }}>
              {canModifyStorage && (
                <Checkbox
                  size="small"
                  checked={allFilesSelected}
                  indeterminate={someFilesSelected && !allFilesSelected}
                  disabled={fileSelectionKeys.length === 0}
                  onChange={(event) => toggleSelectionGroup(fileSelectionKeys, event.target.checked)}
                  inputProps={{ 'aria-label': t('sidebar.storage_select_all_files', 'Select all files') }}
                  sx={{ p: 0.25 }}
                />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                <Typography>{t('sidebar.storage_file_name', 'Name')}</Typography>
                <Tooltip title={t('sidebar.storage_sort_name', 'Sort by name')}>
                  <IconButton size="small" onClick={() => setSortMode(sortMode === 'name_desc' ? 'name' : 'name_desc')} sx={{ p: 0.25, color: '#777c91' }}>
                    {sortMode === 'name_desc' ? <ArrowDownwardIcon sx={{ fontSize: 16 }} /> : <ArrowUpwardIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.25 }}>
                <Typography>{t('sidebar.storage_created', 'Created')}</Typography>
                <Tooltip title={t('sidebar.storage_sort_date', 'Sort by created date')}>
                  <IconButton size="small" onClick={() => setSortMode(sortMode === 'created_desc' ? 'created_asc' : 'created_desc')} sx={{ p: 0.25, color: '#777c91' }}>
                    {sortMode === 'created_desc' ? <ArrowDownwardIcon sx={{ fontSize: 16 }} /> : <ArrowUpwardIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography sx={{ display: { xs: 'none', md: 'block' } }}>{t('sidebar.storage_size', 'Size')}</Typography>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.25 }}>
                <Typography>{t('sidebar.storage_modified', 'Last modified')}</Typography>
                <Tooltip title={t('sidebar.storage_sort_modified', 'Sort by last modified date')}>
                  <IconButton size="small" onClick={() => setSortMode(sortMode === 'modified_desc' ? 'modified_asc' : 'modified_desc')} sx={{ p: 0.25, color: '#777c91' }}>
                    {sortMode === 'modified_desc' ? <ArrowDownwardIcon sx={{ fontSize: 16 }} /> : <ArrowUpwardIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography sx={{ textAlign: 'right' }}>{t('common.actions', 'Actions')}</Typography>
            </Box>
            <List dense disablePadding>
              {sortedFiles.length === 0 ? (
                <ListItem sx={{ minHeight: 180, justifyContent: 'center' }}>
                  <ListItemText
                    sx={{ textAlign: 'center' }}
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    primary={t('sidebar.storage_no_files', 'No files')}
                  />
                </ListItem>
              ) : (
                sortedFiles.map((file) => {
                  const fullPath = buildFullPath(file.name);
                  return (
                    <ListItemButton
                      key={file.name}
                      selected={selectedItems.has(getSelectionKey('file', file.name))}
                      onClick={() => handleOpenViewer(file.name)}
                      sx={{ ...rowSx, gridTemplateColumns: fileGridTemplateColumns }}
                    >
                      {canModifyStorage && (
                        <Checkbox
                          size="small"
                          checked={selectedItems.has(getSelectionKey('file', file.name))}
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) => toggleSelection('file', file.name, event)}
                          inputProps={{ 'aria-label': t('sidebar.storage_select_file', 'Select file') }}
                          sx={{ p: 0.25 }}
                        />
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'grid', placeItems: 'center', width: 27, height: 30, borderRadius: 0.75, backgroundColor: 'rgba(87, 87, 176, 0.10)', color: accentColor, flexShrink: 0 }}>
                          <DescriptionIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Tooltip title={file.name} placement="top-start">
                          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, minWidth: 0 }}>
                            {file.name}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#7c8295', whiteSpace: 'nowrap', display: { xs: 'none', md: 'block' } }}>
                        {formatDate(file.createdAt)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#7c8295', whiteSpace: 'nowrap', display: { xs: 'none', md: 'block' } }}>
                        {formatFileSize(file.size)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#7c8295', whiteSpace: 'nowrap', display: { xs: 'none', md: 'block' } }}>
                        {formatDate(file.modifiedAt || file.modified_at || file.lastModified)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.15 }}>
                        <Tooltip title={t('sidebar.storage_download', 'Download')}>
                          <IconButton size="small" onClick={(event) => { event.stopPropagation(); onDownload(file.name, fullPath); }} sx={rowIconSx}>
                            <DownloadIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        {canModifyStorage && (
                          <>
                            <Tooltip title={t('sidebar.storage_rename_file', 'Rename file')}>
                              <IconButton size="small" onClick={(event) => { event.stopPropagation(); openRenameDialog(file.name, 'file'); }} sx={rowIconSx}>
                                <EditIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('sidebar.storage_delete_file', 'Delete file')}>
                              <IconButton size="small" onClick={(event) => { event.stopPropagation(); openDeleteConfirm(file.name, 'file'); }} sx={rowIconSx}>
                                <DeleteIcon sx={{ fontSize: 17 }} color="error" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </ListItemButton>
                  );
                })
              )}
            </List>
          </Box>
        </Box>
      </>
    );
  };

  return (
    <>
      <input
        type="file"
        multiple
        ref={storageUploadInputRef}
        style={{ display: 'none' }}
        onChange={(event) => {
          const selectedFiles = Array.from(event.target?.files || []);
          if (event.target) {
            event.target.value = '';
          }
          if (!selectedFiles.length) return;
          handleFilesWithConfirm(selectedFiles);
        }}
      />
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={fullScreenDialog}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            boxShadow: '0 24px 70px rgba(20, 24, 74, 0.28)',
            overflow: 'hidden',
            backgroundColor: '#f4f5fb'
          }
        }}
      >
      <DialogTitle
        sx={{
          px: { xs: 2, md: 3 },
          pt: { xs: 2, md: 2.5 },
          pb: { xs: 2, md: 2.25 },
          background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}d9 58%, #7774c5)`,
          color: '#fff'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box sx={{ display: 'grid', placeItems: 'center', width: 42, height: 42, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.23)', flexShrink: 0 }}>
              <StorageIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 850, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                {t('sidebar.storage_browser_title', 'Storage browser')}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.74)', mt: 0.35 }}>
                {restrictToUserFolder ? t('sidebar.storage_personal_space', 'Your personal storage') : t('sidebar.storage_manage_files', 'Manage files and folders')}
              </Typography>
            </Box>
          </Box>
          <Tooltip title={t('common.close', 'Close')}>
            <IconButton onClick={onClose} size="small" sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.3)', flexShrink: 0, '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' } }} aria-label={t('common.close', 'Close')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.78)', fontWeight: 700 }}>
            {t('sidebar.storage_location', 'Location')}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 210, maxWidth: { xs: '100%', sm: 300 } }}>
            <Select
              value={activeRoot || allowedRoots[0] || ''}
              onChange={(e) => onRootChange(e.target.value)}
              displayEmpty
              renderValue={(val) => {
                if (!val && allowedRoots.length === 0) return t('sidebar.storage_root_default', 'Root');
                const label = (val && getStorageRootLabel(val)) || t('sidebar.storage_root_default', 'Root');
                return label;
              }}
              sx={{
                color: '#fff',
                fontSize: '0.82rem',
                fontWeight: 700,
                backgroundColor: 'rgba(255,255,255,0.11)',
                borderRadius: 1.5,
                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.34)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.68)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
                '.MuiSelect-icon': { color: 'rgba(255,255,255,0.85)' },
                height: 36
              }}
            >
              {allowedRoots.map((root) => {
                const label = getStorageRootLabel(root);
                return (
                  <MenuItem key={root} value={root}>
                    {label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
      </DialogTitle>
      {storageUploading && (
        <Box sx={{ px: { xs: 2, md: 3 }, py: 1.25, backgroundColor: '#ecebfa' }}>
          <LinearProgress
            variant="determinate"
            value={storageUploadPercent}
            sx={{
              height: 7,
              borderRadius: 3,
              backgroundColor: 'rgba(87, 87, 176, 0.16)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background: `linear-gradient(90deg, ${primaryColor}, #8a86e0)`
              }
            }}
          />
          <Typography variant="caption" sx={{ display: 'block', color: '#575b76', mt: 0.7, fontWeight: 700 }}>
            {t('sidebar.storage_upload_progress', 'Uploading... {{progress}}%', { progress: Math.round(storageUploadPercent) })}
          </Typography>
        </Box>
      )}
      <DialogContent
        data-file-drop-target="true"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          position: 'relative',
          p: { xs: 1.5, md: 2.5 },
          ...(isDragActive ? { backgroundColor: 'rgba(87, 87, 176, 0.08)' } : null)
        }}
      >
        {isDragActive && (
          <Box
            sx={{
              position: 'absolute',
              inset: 12,
              borderRadius: 2,
              border: '2px dashed rgba(87, 87, 176, 0.5)',
              backgroundColor: 'rgba(255,255,255,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 2
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'rgba(87, 87, 176, 0.9)', fontWeight: 600 }}>
              {t('sidebar.storage_drop_upload', 'Drop files to upload')}
            </Typography>
          </Box>
        )}
        {renderContent()}
      </DialogContent>
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={closeDeleteConfirm}
        >
        <DialogTitle sx={{ pb: 1 }}>
          {t('sidebar.storage_confirm_title', 'Confirm delete')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            {deleteTarget?.type === 'bulk'
              ? t('sidebar.storage_confirm_delete_selected', 'Delete the selected items? Folders and their contents will be removed.')
              : deleteTarget?.type === 'folder'
                ? t('sidebar.storage_confirm_delete_folder', 'Delete this folder and its contents?')
                : t('sidebar.storage_confirm_delete_file', 'Delete this file?')}
          </Typography>
          {deleteTarget?.type === 'bulk' ? (
            <Box sx={{ maxHeight: 180, overflow: 'auto' }}>
              {(deleteTarget.items || []).map((item) => (
                <Typography key={getSelectionKey(item.type, item.name)} variant="body2" sx={{ fontWeight: 700 }}>
                  {item.name}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {deleteTarget?.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm}>{t('common.cancel', 'Cancel')}</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={createOpen}
        onClose={() => { setCreateOpen(false); setNewFolderName(''); }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {t('sidebar.storage_new_folder', 'New folder')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t('sidebar.storage_folder_name', 'Folder name')}
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreateSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateOpen(false); setNewFolderName(''); }}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={!newFolderName.trim()}
          >
            {t('common.create', 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={renameOpen}
        onClose={closeRenameDialog}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {renameTarget?.type === 'folder'
            ? t('sidebar.storage_rename_folder', 'Rename folder')
            : t('sidebar.storage_rename_file', 'Rename file')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t('sidebar.storage_new_name', 'New name')}
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleRenameSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRenameDialog}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleRenameSubmit}>
            {t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
      <StorageBrowserReplaceDialog
        open={confirmReplaceOpen}
        onClose={() => resetPendingUpload()}
        onConfirm={() => {
          const filesToUpload = pendingUploadFiles;
          resetPendingUpload();
          if (filesToUpload.length) {
            handleStorageFiles(filesToUpload);
          }
        }}
        duplicateNames={duplicateUploadNames}
        primaryColor={primaryColor}
        t={t}
      />
      <OtherViewer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        filename={viewerFile?.name || ''}
        filePath={viewerFile?.path || ''}
      />
      <PDFViewer
        open={pdfViewer.open}
        onClose={() => setPdfViewer({ open: false, name: '', path: '' })}
        filePath={pdfViewer.path}
        filename={pdfViewer.name}
      />
      <ImageViewer
        open={imageViewer.open}
        onClose={() => setImageViewer({ open: false, name: '', path: '' })}
        filePath={imageViewer.path}
        filename={imageViewer.name}
      />
      <AudioViewer
        open={audioViewer.open}
        onClose={() => setAudioViewer({ open: false, name: '', path: '' })}
        filePath={audioViewer.path}
        filename={audioViewer.name}
      />
      <ExcelViewer
        open={excelViewer.open}
        onClose={() => setExcelViewer({ open: false, name: '', path: '' })}
        filePath={excelViewer.path}
        filename={excelViewer.name}
      />
      <HTMLViewer
        open={htmlViewer.open}
        onClose={() => setHtmlViewer({ open: false, name: '', path: '' })}
        filePath={htmlViewer.path}
        filename={htmlViewer.name}
      />
      <TextViewer
        open={textViewer.open}
        onClose={() => setTextViewer({ open: false, name: '', path: '' })}
        filePath={textViewer.path}
        filename={textViewer.name}
      />
      <VideoViewer
        open={videoViewer.open}
        onClose={() => setVideoViewer({ open: false, name: '', path: '' })}
        filePath={videoViewer.path}
        filename={videoViewer.name}
      />
      <EmailViewer
        open={emailViewer.open}
        onClose={() => setEmailViewer({ open: false, name: '', path: '' })}
        filePath={emailViewer.path}
        filename={emailViewer.name}
      />
      <ZipViewer
        open={zipViewer.open}
        onClose={() => setZipViewer({ open: false, name: '', path: '' })}
        filePath={zipViewer.path}
        filename={zipViewer.name}
      />
      <WordViewer
        open={wordViewer.open}
        onClose={() => setWordViewer({ open: false, name: '', path: '' })}
        filePath={wordViewer.path}
        filename={wordViewer.name}
      />
      <PowerPointViewer
        open={pptViewer.open}
        onClose={() => setPptViewer({ open: false, name: '', path: '' })}
        filePath={pptViewer.path}
        filename={pptViewer.name}
      />
      <CSVViewer
        open={csvViewer.open}
        onClose={() => setCsvViewer({ open: false, name: '', path: '' })}
        filePath={csvViewer.path}
        filename={csvViewer.name}
      />
      <JSONViewer
        open={jsonViewer.open}
        onClose={closeJsonViewer}
        filePath={jsonViewer.path}
        filename={jsonViewer.name}
      />
    </Dialog>
    </>
  );
};

export default StorageBrowser;
